// Copyright 2020 Drivetrain Hub LLC
// GraphQL handlers for engineering data.

import {capitalizeFirstLetter} from "../../assets/js/utils";
import math from "../../assets/js/math";
import gearEqn from "../../assets/js/gear";
import {mutateUnits} from "./units";
import helper from "./helper";
import lib from "./lib";

// region TOOTH PARAMS

/**
 * Parse tooth data received from a GraphQL API response.
 * @param tooth {object} - Instance of GraphQL Tooth type.
 * @return {{finishedRoot: *, finishedParams: (object|null), prefinishedParams: (object|null)}} Objects resulting from parsed data.
 */
function parseTooth(tooth) {
  return {
    prefinishedParams: tooth.prefinishedParams ? JSON.parse(tooth.prefinishedParams) : null,
    finishedParams: tooth.finishedParams ? JSON.parse(tooth.finishedParams) : null,
    finishedRoot: tooth.finishedRoot,
  }
}

const TOOTH_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // tooth params are served as a GraphQL JSONString, hence snake_case instead of camelCase
  // root params are served as GraphQL fields, hence camelCase
  // PLACEHOLDER {symbols} verify tooth params

  // TOOTH SIZE
  number_of_teeth: ['Number of teeth', '$z$', 'none'],
  module_normal: ['Normal module', '$m_n$', 'length'],
  module_transverse: ['Transverse module', '$m_t$', 'length'],
  diametral_pitch_normal: ['Normal diametral pitch', '$P_n$', 'perInch'],
  diametral_pitch_transverse: ['Transverse diametral pitch', '$P_t$', 'perInch'],

  // PRESSURE & HELIX ANGLES
  pressure_angle_normal: ['Normal pressure angle', '$\\alpha_n$', 'angle'],
  pressure_angle_transverse: ['Transverse pressure angle', '$\\alpha_t$', 'angle'],
  helix_angle: ['Helix angle', '$\\beta$', 'angle'],
  helix_angle_base: ['Helix angle at base diameter', '$\\beta_b$', 'angle'],
  helix_lead: ['Helix lead', '$l_\\beta$', 'length'],

  // TOOTH THICKNESS
  profile_shift_coefficient: ['Profile shift coefficient', '$x^*$', 'none'],
  tooth_thickness_normal: ['Normal tooth thickness', '$s_n$', 'length'],
  tooth_thickness_normal_base: ['Normal tooth thickness at base diameter', '$s_{nb}$', 'length'],
  tooth_thickness_normal_tip: ['Normal tooth thickness at tip diameter', '$s_{na}$', 'length'],
  tooth_thickness_transverse: ['Transverse tooth thickness', '$s_t$', 'length'],
  tooth_thickness_transverse_base: ['Transverse tooth thickness at base diameter', '$s_{tb}$', 'length'],
  tooth_thickness_transverse_tip: ['Transverse tooth thickness at tip diameter', '$s_{tf}$', 'length'],
  half_angle: ['Tooth thickness half angle', '$\\psi$', 'angle'],
  half_angle_tip: ['Tooth thickness half angle at tip diameter', '$\\psi_t$', 'angle'],

  // FACEWIDTH
  facewidth: ['Facewidth', '$b$', 'length'],
  facewidth_effective: ['Effective facewidth', '$b_\\text{eff}$', 'length'],

  // DIAMETRAL
  theoretical_pitch_diameter: ['Theoretical pitch diameter', '$d_p$', 'length'],
  base_diameter: ['Base diameter', '$d_b$', 'length'],
  root_diameter: ['Root diameter', '$d_f$', 'length'],
  tip_diameter: ['Tip diameter', '$d_a$', 'length'],
  // tip_chamfer_diameter: ['Tip chamfer diameter', '$d_{ac}$', 'length'],
  filletBoundaryDiameter: ['Fillet boundary diameter', '$d_F$', 'length'],  // root param
  protuberanceBoundaryDiameter: ['Protuberance boundary diameter', '$d_P$', 'length'],  // root param
  rim_diameter: ['Rim diameter', '$d_r$', 'length'],
  addendum: ['Addendum', '$h_a$', 'length'],
  dedendum: ['Dedendum', '$h_f$', 'length'],
  whole_depth: ['Whole depth', '$h_t$', 'length'],
  addendum_coefficient: ['Addendum coefficient', '$h_a^*$', 'none'],
  dedendum_coefficient: ['Dedendum coefficient', '$h_f^*$', 'none'],
  whole_depth_coefficient: ['Whole depth coefficient', '$h_t^*$', 'none'],

  // ROLL ANGLE / DISTANCE
  theoretical_pitch_roll_angle: ['Theoretical pitch roll angle', '$\\theta_p$', 'angle'],
  tip_roll_angle: ['Tip roll angle', '$\\theta_a$', 'angle'],
  tip_chamfer_roll_angle: ['Tip chamfer roll angle', '$\\theta_{ac}$', 'angle'],
  theoretical_pitch_roll_distance: ['Theoretical pitch roll distance', '', 'length'],
  tip_roll_distance: ['Tip roll distance', '', 'length'],
  tip_chamfer_roll_distance: ['Tip chamfer roll distance', '', 'length'],

  // PITCH
  pitch_normal: ['Normal pitch', '$p_n$', 'length'],
  pitch_transverse: ['Transverse pitch', '$p_t$', 'length'],
  base_pitch_normal: ['Normal base pitch', '$p_{nb}$', 'length'],
  base_pitch_transverse: ['Transverse base pitch', '$p_{tb}$', 'length'],
};
const TOOTH_KEYS = Object.keys(TOOTH_INFO);

/**
 * Lookup the description, symbol and units of a tooth parameter.
 * @param {string} paramStr - Tooth parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Tooth parameter info.
 */
function lookupToothInfo(paramStr) {
  return helper.lookupParamInfo(TOOTH_INFO, TOOTH_KEYS, paramStr);
}

/**
 * Convert units of tooth properties.
 * @param {Object} tooth - Tooth params object.  Function mutates this object *recursively*.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertTooth(tooth, fromUnits, toUnits) {
  return mutateUnits(tooth, TOOTH_INFO, fromUnits, toUnits, true)
}

/**
 * Create an object for tabulating gear tooth parameter data.
 * @param {Object} tooth - Tooth params of a detailed gear object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props for tabular display.
 */
function tableTooth(tooth, units) {
  return lib.libTableParams(tooth, lookupToothInfo, units);
}

function tablePrefinishedParams(tooth, units) {
  return !tooth.prefinishedParams ? [] : tableTooth(tooth.prefinishedParams, units);
}

function tableFinishedParams(tooth, units) {
  return !tooth.finishedParams ? [] : tableTooth(tooth.finishedParams, units);
}

function tableFinishedTooth(tooth, units) {
  return tableTooth({...tooth.finishedParams, ...tooth.finishedRoot}, units);
}

function toothFormDiameter(tooth) {
  // Diameter at the start of involute profile, excluding protuberance involute if applicable.

  return tooth.finishedRoot.protuberanceBoundaryDiameter;
}

function profileLimits(gear) {
  const numberOfTeeth = gear.geom.numberOfTeeth;
  const moduleTransverse = gearEqn.moduleTransverse1(gear.geom);
  const theoreticalPitchDiameter = gearEqn.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gearEqn.pressureAngleTransverse1(gear.geom);
  const baseDiameter = gearEqn.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
  const formDiameter = toothFormDiameter(gear.tooth);

  return {
    formRollAngle: math.rad2deg(gearEqn.diameter2RollAngle(baseDiameter, formDiameter)),
    tipRollAngle: math.rad2deg(gearEqn.diameter2RollAngle(baseDiameter, gear.geom.tipDiameter)),
  }
}

// endregion

// region MESH PARAMS

// PLACEHOLDER {gear types} may need to create separate lookup for each gear mesh type: phasing, concept, detail

const MESH_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // mesh params are served as a GraphQL JSONString, hence snake_case instead of camelCase
  // PLACEHOLDER {symbols} verify mesh params, especially backlash and contact lengths

  // macro
  ratio: ['Gear ratio', '$i$', 'none'],
  working_center_distance: ['Working center distance', '$a_w$', 'length'],
  profile_shift_sum: ['Sum of profile shifts', '$\\Sigma x$', 'length'],
  working_facewidth: ['Working facewidth', '$b_w$', 'length'],
  working_depth: ['Working depth', '$h_k$', 'length'],
  working_helix_angle: ['Working helix angle', '$\\beta_w$', 'angle'],
  working_pressure_angle_transverse: ['Transverse working pressure angle', '$\\alpha_{tw}$', 'angle'],
  working_pressure_angle_normal: ['Normal working pressure angle', '$\\alpha_{nw}$', 'angle'],
  reference_center_distance: ['Reference center distance', '$a_0$', 'length'],
  theoretical_center_distance: ['Theoretical center distance', '$a_t$', 'length'],
  backlash_radial: ['Radial backlash', '$j_r$', 'length'],
  backlash_circumferential: ['Circumferential backlash', '$j_{tt}$', 'length'],
  backlash_profile: ['Profile backlash', '$j_{tn}$', 'length'],
  backlash_normal: ['Normal backlash', '$j_{nn}$', 'length'],
  contact_ratio_axial: ['Axial contact ratio', '$\\epsilon_\\beta$', 'none'],  // also known as "overlap"
  contact_ratio_transverse: ['Transverse contact ratio', '$\\epsilon_\\alpha$', 'none'],  // also known as "radial"
  contact_ratio_total: ['Total contact ratio', '$\\epsilon_\\gamma$', 'none'],
  contact_plane_length: ['Contact plane length', '$L$', 'length'],  // what about symbol: $\\overline{AD}$
  contact_lines_length_mean: ['Mean length of contact lines', '$l_\\text{mean}$', 'length'],
  contact_lines_length_min: ['Minimum length of contact lines', '$l_\\text{min}$', 'length'],

  // micro
  conjugate_relief: ['Conjugate relief', '', 'lengthSmall'],

  // gear1 / gear2
  working_pitch_diameter: ['Working pitch diameter', '$d_w$', 'length'],
  working_pitch_roll_angle: ['Working pitch roll angle', '$\\theta_w$', 'angle'],
  working_pitch_roll_distance: ['Working pitch roll distance', '', 'length'],
  bottom_clearance: ['Bottom clearance', '', 'length'],
  tip_clearance: ['Tip clearance', '', 'length'],
  sap_diameter: ['SAP diameter', '$d_\\text{SAP}$', 'length'],
  sap_roll_angle: ['SAP roll angle', '$\\theta_\\text{SAP}$', 'angle'],
  sap_roll_distance: ['SAP roll distance', '', 'length'],
  sap_specific_sliding: ['SAP specific sliding', '$\\upsilon_\\text{SAP}$', ''],
  eap_diameter: ['EAP diameter', '$d_\\text{EAP}$', 'length'],
  eap_roll_angle: ['EAP roll angle', '$\\theta_\\text{EAP}$', 'angle'],
  eap_roll_distance: ['EAP roll distance', '', 'length'],
  eap_specific_sliding: ['EAP specific sliding', '$\\upsilon_\\text{EAP}$', ''],
};
const MESH_KEYS = Object.keys(MESH_INFO);

/**
 * Lookup the description, symbol and units of a mesh parameter.
 * @param {string} paramStr - Mesh parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Mesh parameter info.
 */
function lookupMeshInfo(paramStr) {
  return helper.lookupParamInfo(MESH_INFO, MESH_KEYS, paramStr);
}

/**
 * Convert units of mesh properties.
 * @param {Object} mesh - Mesh params object.  Function mutates this object *recursively*.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertMesh(mesh, fromUnits, toUnits) {
  return mutateUnits(mesh.params, MESH_INFO, fromUnits, toUnits, true)
}

/**
 * Create an object for tabulating gear mesh parameter data.
 * @param {Object} meshParams - Gear mesh params.  Can be common or gear-specific.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props for tabular display.
 */
function tableMesh(meshParams, units) {
  return lib.libTableParams(meshParams, lookupMeshInfo, units)
}

function tableMeshMacro(mesh, units) {
  return !mesh || !mesh.params.macro ? [] : tableMesh(mesh.params.macro, units)
}

function tableMeshGear1(mesh, units) {
  return !mesh || !mesh.params.gear1 ? [] : tableMesh(mesh.params.gear1, units)
}

function tableMeshGear2(mesh, units) {
  return !mesh || !mesh.params.gear2 ? [] : tableMesh(mesh.params.gear2, units)
}

// endregion

function tableLog(item) {
  // Generate table array for any set of engineering Log warnings and errors.
  // Input arg is expected to have props for `warnings` and `errors`.

  if (!item || !item.errors || !item.warnings) return [];

  function mapEntry(entry) {
    return {
      name: capitalizeFirstLetter(entry.name),
      description: entry.description,
    }
  }

  const errors = item.errors.map(entry => {
    return {
      severity: 'Error',
      ...mapEntry(entry)
    }
  });

  const warnings = item.warnings.map(entry => {
    return {
      severity: 'Warning',
      ...mapEntry(entry)
    }
  });

  return errors.concat(warnings);
}

export default {
  // TOOTH
  parseTooth: parseTooth,
  convertTooth: convertTooth,
  tablePrefinishedParams: tablePrefinishedParams,
  tableFinishedParams: tableFinishedParams,
  tableFinishedTooth: tableFinishedTooth,
  profileLimits: profileLimits,
  // MESH
  convertMesh: convertMesh,
  tableMeshMacro: tableMeshMacro,
  tableMeshGear1: tableMeshGear1,
  tableMeshGear2: tableMeshGear2,
  tableLog: tableLog,
}
